import {
  AssetsTimeInterval,
  UserAssetSnapshot,
  UserInfo,
  ApiResponse,
  OmitFirstArg,
  NotificationCategory,
  NotificationStatus,
  UserNotification,
  NotificationType,
  UserInviteList,
  TransactionType,
  TransactionStatus,
  BaseFetchParams,
} from '../types';
import ky, { KyInstance, Options } from 'ky';

export type LoginBody = {
  address?: string;
  inviteByCode?: string;
  userName?: string;
  avatarUrl?: string;
};

export type LoginApiResult = {
  uid: string;
  isNewUser: boolean;
};

/**
 * signIn
 * (need auth)
 * @param api The KyInstance
 * @param body The sign in body.
 * @param options
 * @returns The sign in response.
 * @example
 * const response = await login({
 *   address: '0x1234567890123456789012345678901234567890',
 *   inviteByCode: 'inviteCode',
 *   userName: 'userName',
 *   avatarUrl: 'avatarUrl',
 * });
 */
export const login = async (api: KyInstance = ky, json: LoginBody, options?: Options) => {
  return await api.post<ApiResponse<LoginApiResult>>('login', { ...options, json }).json();
};

/**
 * Set user avatar
 * (need auth)
 * @param api The KyInstance
 * @param avatarUrl The avatar URL.
 * @param options
 * @returns The set avatar response.
 * @example
 * const response = await setAvatar('avatarUrl');
 */
export const setAvatar = async (api: KyInstance = ky, avatarUrl: string, options?: Options) => {
  return await api.post<ApiResponse>('set-avatar', { ...options, json: { avatarUrl } }).json();
};

/**
 * Set user description
 * (need auth)
 * @param api The KyInstance
 * @param description The description.
 * @param options
 * @returns The set description response.
 * @example
 * const response = await setDescription('description');
 */
export const setDescription = async (api: KyInstance = ky, description: string, options?: Options) => {
  return await api.post<ApiResponse>('set-description', { ...options, json: { description } }).json();
};

export type SetNameAndInviteCodeBody = {
  name?: string;
  inviteByCode?: string;
};

/**
 * Set user name
 * (need auth)
 * @param api The KyInstance
 * @param body The set name and invite code body.
 * @param options
 * @returns The set name response.
 * @example
 * const response = await setNameAndInviteCode({
 *   name: 'name',
 *   inviteByCode: 'inviteCode',
 * });
 */
export const setNameAndInviteCode = async (api: KyInstance = ky, body: SetNameAndInviteCodeBody, options?: Options) => {
  return await api.post<ApiResponse>('set-name', { ...options, json: body }).json();
};

/**
 * Follow user
 * (need auth)
 * @param api The KyInstance
 * @param followUid The follow user uid.
 * @param options
 * @returns The follow user response.
 * @example
 * const response = await followUser('followUid');
 */
export const followUser = async (api: KyInstance = ky, followUid: string, options?: Options) => {
  return await api.post<ApiResponse>('follow-user', { ...options, json: { followUid } }).json();
};

/**
 * Unfollow user
 * (need auth)
 * @param api The KyInstance
 * @param unfollowUid The unfollow user uid.
 * @param options
 * @returns The unfollow user response.
 * @example
 * const response = await unfollowUser('unfollowUid');
 */
export const unfollowUser = async (api: KyInstance = ky, unfollowUid: string, options?: Options) => {
  return await api.post<ApiResponse>('unfollow-user', { ...options, json: { unfollowUid } }).json();
};

export type UserInfoParams = {
  uid?: string;
  email?: string;
  address?: string;
  eoaAddress?: string;
  issuer?: string;
  name?: string;
  inviteCode?: string;
};

/**
 * Get user info
 * (not auth)
 * @param api The KyInstance
 * @param searchParams
 * @param options
 * @returns The user info response.
 * @example
 * const response = await getUserInfo({ uid: '...' });
 */
export const getUserInfo = async (api: KyInstance = ky, searchParams: UserInfoParams = {}, options?: Options) => {
  return await api.get<ApiResponse<UserInfo>>('get-user-base-info', { ...options, searchParams }).json();
};

export type UserAssetsHistoryParams = BaseFetchParams & {
  uid: string;
  timeInterval: AssetsTimeInterval;
};

export type UserAssetsHistory = {
  total: number;
  snapshots: UserAssetSnapshot[];
  baseTokenAddress: string;
  decimal: number;
};

/**
 * Get user assets history
 * (not auth)
 * @param api The KyInstance
 * @param searchParams The user assets history request.
 * @param options
 * @returns The user assets history response.
 * @example
 * const response = await getUserAssetsHistory({
 *   uid: 'uid',
 *   baseTokenAddress: '0x...',
 *   timeInterval: TimeInterval['7d'],
 * });
 */
export const getUserAssetsHistory = async (
  api: KyInstance = ky,
  searchParams: UserAssetsHistoryParams,
  options?: Options
) => {
  return await api.get<ApiResponse<UserAssetsHistory>>('get-user-asset-history', { ...options, searchParams }).json();
};

export type UserNotificationsParams = BaseFetchParams & {
  category?: NotificationCategory;
  status?: NotificationStatus;
  type?: NotificationType;
  page: number;
  pageSize: number;
};

export type UserNotificationsResponse = {
  total: number;
  notifications: UserNotification[];
};
/**
 * Get user notifications
 * (need auth)
 * @param api The KyInstance
 * @param options
 * @returns The user notifications response.
 * @example
 * const response = await getUserNotifications();
 */
export const getUserNotifications = async (
  api: KyInstance = ky,
  searchParams: UserNotificationsParams,
  options?: Options
) => {
  return await api
    .get<ApiResponse<UserNotificationsResponse>>('get-user-notifications', { ...options, searchParams })
    .json();
};

export type MarkUserNotificationParams = BaseFetchParams & {
  notificationUuids: string[];
};

/**
 * Mark user notification
 * (need auth)
 * @param api The KyInstance
 * @param options
 * @returns The mark user notification response.
 * @example
 * const response = await markUserNotification({
 *   notificationUuids: 'notificationUuids',
 * });
 */
export const markUserNotification = async (api: KyInstance = ky, json: MarkUserNotificationParams, options?: Options) => {
  return await api.post<ApiResponse>('mark-notifications-as-read', { ...options, json }).json();
};

export type UserInviteListParams = BaseFetchParams & {
  page: number;
  pageSize: number;
};

/**
 * Get user invite list
 * (need auth)
 * @param api The KyInstance
 * @param searchParams
 * @param options
 * @returns The user invite list response.
 * @example
 * const response = await getUserInviteList({
 *   page: 1,
 *   pageSize: 10,
 * });
 */
export const getUserInviteList = async (api: KyInstance = ky, searchParams: UserInviteListParams, options?: Options) => {
  return await api.get<ApiResponse<UserInviteList>>('get-invite-user-list', { ...options, searchParams }).json();
};

export type UserTransactionParams = BaseFetchParams & {
  page: number;
  pageSize: number;
};

export type UserTransaction = {
  type: TransactionType;
  uid: string;
  timestamp: string;
  side: number;
  tokenAddress: string;
  decimal: number;
  amount: string;
  status: TransactionStatus;
  bizDataJson: string;
  txHash: string;
};

export type UserTransactionResult = {
  total: number;
  transactions: UserTransaction[];
};

/**
 * Get user transactions
 * (need auth)
 * @param api The KyInstance
 * @param searchParams
 * @param options
 * @returns The user transactions response.
 * @example
 * const response = await getUserTransactions({
 *   page: 1,
 *   pageSize: 10,
 * });
 */
export const getUserTransactions = async (api: KyInstance = ky, searchParams: UserTransactionParams, options?: Options) => {
  return await api.get<ApiResponse<UserTransactionResult>>('get-user-transactions', { ...options, searchParams }).json();
};

/**
 * Per-currency accrual of the deployed 0.5% fee-share referral program.
 * All amounts are raw decimal strings at the token's `decimals` precision
 * (18dp on DPIN) — format with a bigint-safe formatter, never Number().
 */
export type ReferralEarningToken = {
  /** Token symbol or identifier the accrual is denominated in (e.g. DPS/DPN). */
  currency: string;
  /** Token decimals (18 on DPIN). */
  decimals: number;
  /** Lifetime accrued fee-share, raw string at `decimals`. */
  accrued: string;
  /** Already paid out, raw string at `decimals`. */
  paid: string;
  /** Currently claimable, raw string at `decimals`. */
  claimable: string;
  /** Accrued but withheld until the user binds a wallet, raw string at `decimals`. */
  pendingWalletBind: string;
};

export type ReferralEarnings = {
  /** Wallet the fee-share is (or will be) paid to; null until bound. */
  boundWallet: string | null;
  /** Whether on-chain claim is currently open. */
  claimEnabled: boolean;
  tokens: ReferralEarningToken[];
};

/**
 * Get the signed-in user's fee-share referral earnings.
 * (need auth)
 * @param api The KyInstance (bound to the `/referral` prefix)
 * @param options
 * @returns The referral earnings response.
 * @example
 * const response = await getReferralEarnings();
 */
export const getReferralEarnings = async (api: KyInstance = ky, options?: Options) => {
  return await api.get<ApiResponse<ReferralEarnings>>('my-earnings', options).json();
};

export type UserApi = {
  /**
   * signIn
   * (need auth)
   * @param api The KyInstance
   * @param body The sign in body.
   * @param options
   * @returns The sign in response.
   * @example
   * const response = await login({
   *   address: '0x1234567890123456789012345678901234567890',
   *   inviteByCode: 'inviteCode',
   *   userName: 'userName',
   *   avatarUrl: 'avatarUrl',
   * });
   */
  login: OmitFirstArg<typeof login>;
  /**
   * Set user avatar
   * (need auth)
   * @param api The KyInstance
   * @param avatarUrl The avatar URL.
   * @param options
   * @returns The set avatar response.
   * @example
   * const response = await setAvatar('avatarUrl');
   */
  setAvatar: OmitFirstArg<typeof setAvatar>;
  /**
   * Set user description
   * (need auth)
   * @param api The KyInstance
   * @param description The description.
   * @param options
   * @returns The set description response.
   * @example
   * const response = await setDescription('description');
   */
  setDescription: OmitFirstArg<typeof setDescription>;
  /**
   * Set user name
   * (need auth)
   * @param api The KyInstance
   * @param name The name.
   * @param options
   * @returns The set name response.
   * @example
   * const response = await setNameAndInviteCode({
   *   name: 'name',
   *   inviteByCode: 'inviteCode',
   * });
   */
  setNameAndInviteCode: OmitFirstArg<typeof setNameAndInviteCode>;
  /**
   * Follow user
   * (need auth)
   * @param api The KyInstance
   * @param followUid The follow user uid.
   * @param options
   * @returns The follow user response.
   * @example
   * const response = await followUser('followUid');
   */
  followUser: OmitFirstArg<typeof followUser>;
  /**
   * Unfollow user
   * (need auth)
   * @param api The KyInstance
   * @param unfollowUid The unfollow user uid.
   * @param options
   * @returns The unfollow user response.
   * @example
   * const response = await unfollowUser('unfollowUid');
   */
  unfollowUser: OmitFirstArg<typeof unfollowUser>;
  /**
   * Get user info
   * (not auth)
   * @param api The KyInstance
   * @param searchParams
   * @param options
   * @returns The user info response.
   * @example
   * const response = await getUserInfo({baseTokenAddress:0xx});
   */
  getUserInfo: OmitFirstArg<typeof getUserInfo>;
  /**
   * Get user assets history
   * (not auth)
   * @param api The KyInstance
   * @param searchParams The user assets history request.
   * @param options
   * @returns The user assets history response.
   * @example
   * const response = await getUserAssetsHistory({
   *   uid: 'uid',
   *   baseTokenAddress: '0x...',
   *   timeInterval: TimeInterval['7d'],
   * });
   */
  getUserAssetsHistory: OmitFirstArg<typeof getUserAssetsHistory>;

  /**
   * Get user notifications
   * (need auth)
   * @param api The KyInstance
   * @param options
   * @returns The user notifications response.
   * @example
   * const response = await getUserNotifications();
   */
  getUserNotifications: OmitFirstArg<typeof getUserNotifications>;
  /**
   * Mark user notification
   * (need auth)
   * @param api The KyInstance
   * @param options
   * @returns The mark user notification response.
   * @example
   * const response = await markUserNotification({
   *   notificationUuids: 'notificationUuids',
   * });
   */
  markUserNotification: OmitFirstArg<typeof markUserNotification>;
  /**
   * Get user invite list
   * (need auth)
   * @param api The KyInstance
   * @param searchParams
   * @param options
   * @returns The user invite list response.
   * @example
   * const response = await getUserInviteList({
   *   page: 1,
   *   pageSize: 10,
   * });
   */
  getUserInviteList: OmitFirstArg<typeof getUserInviteList>;

  /**
   * Get user transactions
   * (need auth)
   * @param api The KyInstance
   * @param searchParams
   * @param options
   * @returns The user transactions response.
   * @example
   * const response = await getUserTransactions({
   *   page: 1,
   *   pageSize: 10,
   * });
   */
  getUserTransactions: OmitFirstArg<typeof getUserTransactions>;

  /**
   * Get the signed-in user's fee-share referral earnings.
   * (need auth)
   * @param options
   * @returns The referral earnings response.
   * @example
   * const response = await getReferralEarnings();
   */
  getReferralEarnings: OmitFirstArg<typeof getReferralEarnings>;
};
export const createUserApi = (initOptions: Options): UserApi => {
  const margeOptions = { ...initOptions, prefixUrl: `${initOptions.prefixUrl}/user` } as Options;
  const userApi = ky.create(margeOptions);
  // Referral earnings live under a sibling `/referral` prefix (not `/user`), so
  // they need their own ky instance — mirrors how points.ts splits user/points.
  const referralApi = ky.create({ ...initOptions, prefixUrl: `${initOptions.prefixUrl}/referral` } as Options);
  return {
    login: login.bind(null, userApi),
    setAvatar: setAvatar.bind(null, userApi),
    setDescription: setDescription.bind(null, userApi),
    setNameAndInviteCode: setNameAndInviteCode.bind(null, userApi),
    followUser: followUser.bind(null, userApi),
    unfollowUser: unfollowUser.bind(null, userApi),
    getUserInfo: getUserInfo.bind(null, userApi),
    getUserAssetsHistory: getUserAssetsHistory.bind(null, userApi),
    getUserNotifications: getUserNotifications.bind(null, userApi),
    markUserNotification: markUserNotification.bind(null, userApi),
    getUserInviteList: getUserInviteList.bind(null, userApi),
    getUserTransactions: getUserTransactions.bind(null, userApi),
    getReferralEarnings: getReferralEarnings.bind(null, referralApi),
  };
};

export default createUserApi;
